/**
 * 버튼 시각 클래스 생성기(CVA) — BaseButton 의 시각 SSoT.
 *
 * BaseButton.vue(`<script setup>`) 는 값 export 가 불가하므로(vue/no-export-in-script-setup)
 * CVA 정의를 여기로 분리한다. 실제 <button> 은 BaseButton 이, <RouterLink>·<a> 를
 * 버튼처럼 보이게 할 때는 소비처가 이 유틸을 직접 사용해 **링크 안에 <button> 을
 * 중첩(nested interactive)하지 않고** 단일 interactive 요소로 스타일만 입힌다(접근성 트리 보존).
 *
 * 색은 전부 토큰(Tailwind 매핑색) 경유 — hex 직접 박지 않음.
 */
import { cva, type VariantProps } from 'class-variance-authority';

export const buttonVariants = cva(
  // .btn 공통: inline-flex 정렬 · 700 · radius 7 · transition · 1.5px 투명 보더
  // 키보드 포커스 링(focus-visible) — 마우스 클릭엔 안 뜨고 Tab 이동 시만(접근성)
  'fo-btn inline-flex items-center justify-center gap-[9px] rounded-[7px] border-[1.5px] border-transparent font-sans font-bold tracking-[-0.01em] transition-[0.16s] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
  {
    variants: {
      variant: {
        // .btn-gold — 골드 배경 + 골드 그림자(--shadow-accent-btn 토큰), hover 시 gold-deep
        accent: 'bg-accent text-white shadow-accent-btn hover:bg-accent-deep',
        // .btn-navy (기본) — 네이비 배경, hover navy-2
        default: 'bg-brand text-white hover:bg-brand-2',
        // .btn-line — 흰 배경 + blue-line 보더, hover navy 보더
        line: 'border-brand-line-2 bg-white text-brand hover:border-brand',
        // .btn-linelight — 다크 배경 위 투명, 반투명 흰 보더
        linelight: 'border-white/45 bg-transparent text-white hover:border-white hover:bg-white/10',
      },
      size: {
        // 목록·인라인 보조 액션 — 패딩 7/14 · 13.5px (배지 옆 등 가벼운 액션)
        sm: 'px-3.5 py-[7px] text-[13.5px]',
        // .btn 기본 패딩 13/24 · 15px
        md: 'px-6 py-[13px] text-[15px]',
        // .btn.lg — 패딩 16/30 · 15.5px
        lg: 'px-[30px] py-4 text-[15.5px]',
      },
      block: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'accent',
      size: 'md',
      block: false,
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

/** 버튼 시각 변형 — 디자인 시스템 카탈로그 등에서 재사용. */
export type ButtonVariant = NonNullable<ButtonVariantProps['variant']>;
